import {
    FORM_NAME_WRITING_PRODUCT_REVIEW,
    NAME_PRODUCT_REVIEW_WARNING_CLOSE_DIALOG,
    NAME_WRITING_PRODUCT_REVIEW_DIALOG
} from 'PyzShopUi/scripts/product-review/constants/forms';
import FormUtils from 'PyzShopUi/scripts/utils/formUtils';
import { productReviewService } from 'PyzShopUi/scripts/product-review/services/ProductReviewService';
import EventBus from 'PyzShopUi/scripts/utils/eventBus';
import { EDialogEvents } from 'PyzShopUi/scripts/modules/events/dialogEvent';
import { DIALOG_OFFSET } from 'PyzShopUi/scripts/product-review/constants';
import { customerVerificationService } from 'PyzShopUi/scripts/product-review/services/CustomerVerificationService';

export class WritingProductReviewFormHandler {
    private static instance: WritingProductReviewFormHandler = null;

    private eventBus: EventBus;
    private isFormDirty: boolean;

    private readonly dialogElement: HTMLElement;
    private readonly closeButtonElement: HTMLElement;
    private readonly formFieldElements: NodeListOf<HTMLElement>;
    private readonly writingFormElement: HTMLFormElement;
    private readonly submitButtonElement: HTMLButtonElement;
    private readonly errorMessageElement: HTMLElement;
    private readonly errorMessageTextElement: HTMLElement;
    private readonly dialogContentElement: HTMLElement;

    public static getInstance(): WritingProductReviewFormHandler {
        if (this.instance === null) {
            this.instance = new WritingProductReviewFormHandler();
        }

        return this.instance;
    }

    constructor() {
        this.eventBus = EventBus.getInstance();

        this.dialogElement = document.querySelector(`#${NAME_WRITING_PRODUCT_REVIEW_DIALOG}`);
        this.closeButtonElement = this.dialogElement.querySelector('.mdc-dialog__close');
        this.errorMessageElement = this.dialogElement.querySelector('.error-message-container');
        this.errorMessageTextElement = this.dialogElement.querySelector('.error-message-container p');
        this.writingFormElement = this.dialogElement.querySelector(`[name="${FORM_NAME_WRITING_PRODUCT_REVIEW}"]`);
        this.formFieldElements = this.dialogElement.querySelectorAll('input,textarea');
        this.dialogContentElement = this.errorMessageElement.closest('.mdc-dialog__content');
        this.submitButtonElement = this.writingFormElement.querySelector('button[type="submit"]');

        this.onWritingFormChange();
        this.onWritingFormCancel();
        this.onWritingFormSubmit();
    }

    public initForm() {
        if (!customerVerificationService.isValid()) {
            return;
        }

        const formNames = customerVerificationService.getMappedParamsToFormNames();
        this.isFormDirty = true;

        this.formFieldElements.forEach((field: HTMLFormElement) => {
            const formValue = formNames[field.name];
            if (formValue) {
                field.value = formValue;
                field.focus();
            }
        });
    }

    private onWritingFormSubmit() {
        this.writingFormElement.addEventListener('submit', (formEvent: SubmitEvent) => {
            formEvent.preventDefault();

            FormUtils.disableButton(this.submitButtonElement)

            const formData = new FormData(this.writingFormElement);

            productReviewService.createProductReview(formData)
                .then(response => {
                    if (response.isSuccess) {
                        this.handleSuccess();
                    } else {
                        const errorMessage = response?.errors?.[0]?.message;
                        this.handleError(errorMessage);

                    }
                })
                .catch((error) => {
                    const errorMessage = error?.errors?.[0]?.message;
                    this.handleError(errorMessage);
                });

            return false;
        });
    }

    private handleSuccess() {
        this.resetForm();

        FormUtils.enableButton(this.submitButtonElement);
        this.eventBus.publish(EDialogEvents.CLOSE_DIALOG, { id: NAME_WRITING_PRODUCT_REVIEW_DIALOG })
        this.eventBus.publish(EDialogEvents.OPEN_DIALOG, { id: 'success-writing-review-dialog' });
    }

    private handleError(errorMessage: string) {
        FormUtils.enableButton(this.submitButtonElement);
        if (errorMessage) {
            this.errorMessageTextElement.innerText = errorMessage;
        }
        this.errorMessageElement.classList.remove('hide');

        this.eventBus.publish('scrolling:scrollTo', {
            target: this.errorMessageElement,
            elementToScroll: this.dialogContentElement,
            offset: DIALOG_OFFSET,
        })
    }

    private onWritingFormCancel() {
        this.closeButtonElement.onclick = () => {
            if (!this.isFormDirty) {
                this.resetForm();
                this.eventBus.publish(EDialogEvents.CLOSE_DIALOG, {
                    id: NAME_WRITING_PRODUCT_REVIEW_DIALOG
                });

                return;
            }

            this.eventBus.publish(EDialogEvents.OPEN_DIALOG, {
                id: NAME_PRODUCT_REVIEW_WARNING_CLOSE_DIALOG
            });
        };

        this.eventBus.subscribe(EDialogEvents.OPEN_DIALOG, (target: HTMLElement) => {
            if (target.id !== NAME_PRODUCT_REVIEW_WARNING_CLOSE_DIALOG) {
                return;
            }

            const continueButton: HTMLButtonElement = document
                .querySelector(`#${NAME_PRODUCT_REVIEW_WARNING_CLOSE_DIALOG} [data-mdc-dialog-action="continue"]`);

            continueButton.onclick = () => {
                this.resetForm();
                this.eventBus.publish(EDialogEvents.CLOSE_DIALOG, {
                    id: NAME_PRODUCT_REVIEW_WARNING_CLOSE_DIALOG
                });
                this.eventBus.publish(EDialogEvents.CLOSE_DIALOG, {
                    id: NAME_WRITING_PRODUCT_REVIEW_DIALOG
                });
            };
        });
    }

    private resetForm() {
        this.isFormDirty = false;
        this.errorMessageElement.classList.add('hide');
        FormUtils.resetForm(this.writingFormElement);
    }

    private onWritingFormChange() {
        this.formFieldElements.forEach((element: HTMLInputElement) =>
            element.onchange = () =>
                this.isFormDirty = true
        );
    }
}
